import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { fmtSlots, slotsOf, fmtDateTime, relTime } from "../lib/format";
import { Pill, PriceTag, StockBar, Thumb, EventText } from "../components/bits";
import { Rocket, CalendarClock, PencilLine, Archive, CopyPlus } from "lucide-react";
import { useFlash } from "../lib/flash";
import { apiError } from "../lib/auth";
import KpiStrip, { KpiSkeleton } from "../components/KpiStrip";
import FbHealthBanner, { fbBroken } from "../components/FbHealth";
import Onboarding from "../components/Onboarding";
import PushPrompt from "../components/PushPrompt";
import LegalNotice from "../components/LegalNotice";
import TrialBanner from "../components/TrialBanner";
import { RepostCards } from "./RepostReview";
import BulkReminderBar from "../components/BulkReminderBar";
import SmsQuotaBanner from "../components/SmsQuotaBanner";
import { OfferRevenue, TabSummary } from "../components/OfferRevenue";
import OfferMenu from "../components/OfferMenu";
import { useAuth } from "../lib/auth";

const TABS = [
  { id: "active", label: "Aktive", icon: Rocket },
  { id: "upcoming", label: "Kommende", icon: CalendarClock },
  { id: "drafts", label: "Kladder", icon: PencilLine },
  { id: "archive", label: "Arkiv", icon: Archive },
];
const EMPTY = {
  active: "Ingen aktive tilbud lige nu.",
  upcoming: "Ingen planlagte opslag. Opret et tilbud, og vælg et tidspunkt, det skal slås op.",
  drafts: "Ingen kladder. Gem et tilbud som kladde, så ligger det her klar.",
  archive: "Dit arkiv er tomt. Afsluttede tilbud lander her, når afhentningen er ovre.",
};

const SORTS = [
  ["newest", "Nyeste"],
  ["revenue", "Mest omsat"],
  ["rate", "Højest afhentning"],
];
const sorters = {
  revenue: (a, b) => (b.sales?.revenue || 0) - (a.sales?.revenue || 0),
  rate: (a, b) => (b.sales?.pickup_rate ?? -1) - (a.sales?.pickup_rate ?? -1),
};

function ReuseButton({ id }) {
  const nav = useNavigate();
  const flash = useFlash();
  const [busy, setBusy] = useState(false);
  const reuse = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.post(`/offers/${id}/duplicate`);
      flash(r.data.flash);
      nav(`/offers/${r.data.id}/edit`);
    } catch (err) {
      flash(apiError(err.response?.data?.detail) || err.message, "error");
      setBusy(false);
    }
  };
  return (
    <button type="button" className="btn small reuse-btn" onClick={reuse} disabled={busy} data-testid={`offer-reuse-${id}`}>
      <CopyPlus size={16} /> {busy ? "Kopierer…" : "Genbrug"}
    </button>
  );
}

export function OfferRows({ cards, tab, onChange, select, reminderTag }) {
  const { store } = useAuth();
  const canDelete = store && !store.staff;
  return cards.map(({ offer, stats, state, sales, res_count }) => (
    <Link className={"offer-row" + (select ? " selectable" : "") + (select?.ids.has(offer.id) ? " selected" : "")} to={`/offers/${offer.id}`}
      key={offer.id} data-testid={`offer-row-${offer.id}`}
      onClick={select ? (e) => { e.preventDefault(); select.toggle(offer.id); } : undefined}>
      {select && (
        <input type="checkbox" className="row-check" checked={select.ids.has(offer.id)} readOnly tabIndex={-1}
          aria-label={`Vælg ${offer.title}`} data-testid={`offer-select-${offer.id}`} />
      )}
      <Thumb offer={offer} />
      <div>
        <h3>{offer.title}</h3>
        <div className="meta">
          {offer.status === "scheduled" && (
            <>
              <b>Slås op {fmtDateTime(offer.publish_at)}</b>
              {" · "}
            </>
          )}
          Afhentning {fmtSlots(slotsOf(offer))}
          {reminderTag && offer.comment_reminder_off ? (
            <span className="cr-off-tag" data-testid={`offer-cr-off-tag-${offer.id}`}>Påmindelse fra</span>
          ) : null}
        </div>
        <div style={{ marginTop: 8, maxWidth: 420 }}>
          <StockBar stats={stats} />
        </div>
        <OfferRevenue id={offer.id} sales={sales} tab={tab} />
      </div>
      <div className="right">
        <span>
          {offer.published_test ? (
            <>
              <Pill status="draft" label="Test" />{" "}
            </>
          ) : null}
          {offer.copy_pending ? (
            <>
              <Pill status="draft" label="Kopi" />{" "}
            </>
          ) : null}
          <Pill status={state.cls} label={state.label} />
          {canDelete && <OfferMenu offer={offer} count={res_count} onChange={onChange} />}
        </span>
        <PriceTag offer={offer} />
        {tab === "archive" && <ReuseButton id={offer.id} />}
      </div>
    </Link>
  ));
}

export default function Dashboard() {
  const { store } = useAuth();
  const [d, setD] = useState(null);
  const [tab, setTabRaw] = useState("active");
  const [sort, setSort] = useState("newest");
  const [sel, setSel] = useState(null);
  const setTab = (t) => { setTabRaw(t); setSel(null); };
  const toggleSel = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const [reposts, setReposts] = useState([]);
  const load = () => {
    api.get("/reposts").then((r) => setReposts(r.data.suggestions)).catch(() => {});
    return api.get("/dashboard").then((r) => setD(r.data));
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, []);

  if (!d)
    return (
      <>
        <div className="page-head">
          <div>
            <h1>Overblik</h1>
            <p>&nbsp;</p>
          </div>
        </div>
        <KpiSkeleton />
      </>
    );
  const k = d.kpis;
  const lists = { active: d.active, upcoming: d.upcoming, drafts: d.drafts, archive: d.archive };
  const base = lists[tab] || [];
  const cards = tab === "archive" && sorters[sort] ? [...base].sort(sorters[sort]) : base;
  const canBulk = !!d.comment_reminder_shop && !store?.staff;
  const selectable = (o) => (tab === "active" || tab === "upcoming") && !o.published_test;
  const showOffers = () => {
    setTab("active");
    document.getElementById("dash-offers")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Overblik</h1>
          <p>{d.date}</p>
        </div>
        <div className="actions">
          <Link className="btn" to="/pickup">
            Åbn udlevering
          </Link>
          <Link className="btn primary" to="/offers/new" data-testid="new-offer-btn">
            Nyt ja tak-tilbud
          </Link>
        </div>
      </div>
      <TrialBanner />
      <FbHealthBanner health={d.fb_health} pageName={d.fb_page_name} />
      <SmsQuotaBanner q={d.sms_quota} onChange={load} />
      <Onboarding data={d.onboarding} bannerShown={fbBroken(d.fb_health)} />
      <LegalNotice />
      <PushPrompt />
      <KpiStrip k={k} onOffers={showOffers} />
      <RepostCards items={reposts} />

      <div className="grid cols-main">
        <div>
          <section className="panel" id="dash-offers">
            <div className="dash-tabs" role="tablist" data-testid="dashboard-tabs">
              {TABS.map((t) => {
                const Icon = t.icon;
                const n = (lists[t.id] || []).length;
                return (
                  <button
                    key={t.id}
                    role="tab"
                    aria-selected={tab === t.id}
                    className={"dash-tab" + (tab === t.id ? " active" : "")}
                    onClick={() => setTab(t.id)}
                    data-testid={`dash-tab-${t.id}`}
                  >
                    <Icon size={17} />
                    <span>{t.label}</span>
                    {n > 0 && <b className="dash-count">{n}</b>}
                  </button>
                );
              })}
            </div>
            <div className="orv-bar">
              <TabSummary tab={tab} label={TABS.find((t) => t.id === tab).label} cards={cards} />
              {canBulk && !sel && cards.some((c) => selectable(c.offer)) && (
                <button type="button" className="btn small" onClick={() => setSel(new Set())} data-testid="bulk-select-btn">
                  Vælg
                </button>
              )}
              {tab === "archive" && cards.length > 1 && (
                <label className="orv-sort">
                  <span>Sortér</span>
                  <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="archive-sort">
                    {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </label>
              )}
            </div>
            {sel && <BulkReminderBar ids={sel} onCancel={() => setSel(null)} onDone={() => { setSel(null); load(); }} />}
            {cards.length ? (
              <OfferRows cards={cards} tab={tab} onChange={load} reminderTag={canBulk}
                select={sel ? { ids: sel, toggle: (id) => cards.some((c) => c.offer.id === id && selectable(c.offer)) && toggleSel(id) } : null} />
            ) : (
              <div className="empty">
                <p>{EMPTY[tab]}</p>
                {tab !== "archive" && (
                  <Link className="btn jatak" to="/offers/new" data-testid="empty-new-offer-btn">
                    Nyt ja tak-tilbud
                  </Link>
                )}
              </div>
            )}
          </section>
        </div>
        <aside className="panel" aria-live="polite">
          <h2>Seneste aktivitet</h2>
          {d.events.length ? (
            <ul className="feed">
              {d.events.map((ev) => (
                <li className={`t-${ev.type}`} key={ev.id}>
                  <span><EventText ev={ev} /></span>
                  <time>{relTime(ev.created_at)}</time>
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted">Her kommer kundernes "ja tak", afbud og afhentninger til at stå.</p>
          )}
        </aside>
      </div>
    </>
  );
}
